import { Head, Link } from "@inertiajs/react"
import {
  Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography
} from "@mui/material"
import AdminLayout from "../../../layouts/AdminLayout"

type ConversationStatus = "draft" | "accepted" | "completed"

type Conversation = {
  id: number
  title: string
  description: string | null
  status: ConversationStatus
  topic: { id: number; name: string } | null
  line_count: number
}

const STATUS_COLOR: Record<ConversationStatus, "default" | "warning" | "success"> = {
  draft: "default",
  accepted: "warning",
  completed: "success"
}

export function StatusChip({ status }: { status: ConversationStatus }) {
  return <Chip size="small" label={status} color={STATUS_COLOR[status] ?? "default"} />
}

export default function AdminConversationsIndex({ conversations }: { conversations: Conversation[] }) {
  return (
    <AdminLayout title="Conversations" maxWidth={false}>
      <Head title="Admin · Conversations" />
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">Conversations</Typography>
        <Button component={Link} href="/admin/conversations/new" variant="contained"
                data-testid="new-conversation">
          New conversation
        </Button>
      </Stack>
      {conversations.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }} data-testid="conversations-empty">
          <Typography>No conversations yet.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table data-testid="conversations-table">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Topic</TableCell>
                <TableCell>Lines</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.map((conversation) => (
                <TableRow key={conversation.id} data-testid={`conversation-row-${conversation.id}`}>
                  <TableCell>
                    <Link href={`/admin/conversations/${conversation.id}`}>{conversation.title}</Link>
                  </TableCell>
                  <TableCell><StatusChip status={conversation.status} /></TableCell>
                  <TableCell>{conversation.topic?.name ?? "—"}</TableCell>
                  <TableCell>{conversation.line_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AdminLayout>
  )
}
